import React, { useState, useEffect, useCallback } from "react";
import {
  Megaphone,
  Send,
  Pin,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Users,
  Eye,
  X,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const FIELDIQ_API_URL =
  process.env.REACT_APP_FIELDIQ_API_URL ||
  "https://fieldiq-api.hyveappliedintelligence.com";

const REACTIONS = ["👍", "✅", "❓", "❤️"];

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Broadcast card (list view) ────────────────────────────────────────────────

function BroadcastCard({ broadcast }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {broadcast.priority === "urgent" && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-rose-100 text-rose-700 rounded-full flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Urgent
                </span>
              )}
              {broadcast.pinned_until && (
                <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                  <Pin className="w-3 h-3" /> Pinned
                </span>
              )}
              <span className="text-xs text-slate-400">
                {timeAgo(broadcast.sent_at)}
              </span>
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Users className="w-3 h-3" /> {broadcast.read_count ?? 0} read
              </span>
            </div>
            <p className="text-sm font-semibold text-slate-800">
              {broadcast.title}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              Sent by {broadcast.sender_name}
            </p>
          </div>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </button>
        </div>

        {expanded && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
              {broadcast.body}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const BroadcastComposer = () => {
  const { token } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("");
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Compose state
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [pinDays, setPinDays] = useState("0");
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_AUTH_URL || "/auth"}/facilities`, {
      headers,
    })
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setFacilities(list);
        if (list.length > 0) setSelectedFacility(list[0].facility_id || "");
      })
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBroadcasts = useCallback(async () => {
    if (!selectedFacility) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${FIELDIQ_API_URL}/broadcasts?ld_id=${encodeURIComponent(selectedFacility)}`,
        { headers },
      );
      if (res.ok) {
        const data = await res.json();
        setBroadcasts([...(data.pinned || []), ...(data.feed || [])]);
      }
    } catch {}
    setLoading(false);
  }, [token, selectedFacility]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchBroadcasts();
  }, [fetchBroadcasts]);

  const handleSend = async () => {
    setSending(true);
    try {
      const pinnedUntil =
        pinDays !== "0"
          ? new Date(Date.now() + Number(pinDays) * 86400000).toISOString()
          : null;
      const res = await fetch(`${FIELDIQ_API_URL}/broadcasts`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ld_id: selectedFacility,
          title: title.trim(),
          body: body.trim(),
          priority,
          pinned_until: pinnedUntil,
        }),
      });
      if (res.ok) {
        setSentSuccess(true);
        setTitle("");
        setBody("");
        setPriority("normal");
        setPinDays("0");
        setShowConfirm(false);
        setTimeout(() => setSentSuccess(false), 4000);
        fetchBroadcasts();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`Send failed: ${err.detail || res.status}`);
      }
    } catch {
      alert("Network error — broadcast not sent.");
    }
    setSending(false);
  };

  const canSend =
    title.trim().length > 0 && body.trim().length > 0 && selectedFacility;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Megaphone className="w-5 h-5 text-violet-600" />
          <div>
            <h1 className="text-lg font-semibold text-slate-800">Broadcasts</h1>
            <p className="text-sm text-slate-500">
              Send announcements to all users. One-way, no replies.
            </p>
          </div>
        </div>
        <button
          onClick={fetchBroadcasts}
          disabled={loading}
          className="px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm flex items-center gap-1.5"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Success toast */}
      {sentSuccess && (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          Broadcast sent — all users in {selectedFacility} will receive a push
          notification.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Compose panel */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-slate-700">
            Compose Broadcast
          </h2>

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              District (LD)
            </label>
            <select
              value={selectedFacility}
              onChange={(e) => setSelectedFacility(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
            >
              <option value="">Select district...</option>
              {facilities.map((f) => (
                <option key={f.facility_id} value={f.facility_id}>
                  {f.facility_name || f.facility_id}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Title
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
              placeholder="Brief, clear subject line..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Message
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              placeholder="Write your announcement here. Be clear and actionable. PCs cannot reply."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-violet-500"
            />
            <p className="text-xs text-slate-400 mt-1">{body.length} chars</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Priority
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
              >
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Pin for
              </label>
              <select
                value={pinDays}
                onChange={(e) => setPinDays(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
              >
                <option value="0">No pin</option>
                <option value="1">1 day</option>
                <option value="3">3 days</option>
                <option value="7">7 days</option>
                <option value="30">30 days</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowPreview(true)}
              disabled={!canSend}
              className="flex-1 px-4 py-2.5 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <Eye className="w-4 h-4" /> Preview
            </button>
            <button
              onClick={() => setShowConfirm(true)}
              disabled={!canSend}
              className="flex-1 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <Send className="w-4 h-4" /> Send Broadcast
            </button>
          </div>

          <p className="text-xs text-slate-400 text-center">
            Broadcasts cannot be edited after sending. Reactions:{" "}
            {REACTIONS.join(" ")}
          </p>
        </div>

        {/* Sent broadcasts list */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-700">
            Sent Broadcasts
            {selectedFacility && (
              <span className="ml-2 text-slate-400 font-normal">
                — {selectedFacility}
              </span>
            )}
          </h2>
          {loading ? (
            <div className="text-center py-8 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            </div>
          ) : broadcasts.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 text-sm">
              No broadcasts sent yet.
            </div>
          ) : (
            <div className="space-y-2">
              {broadcasts.map((b) => (
                <BroadcastCard key={b.id} broadcast={b} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Preview modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-slate-900 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2">
                {priority === "urgent" && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-rose-900/60 text-rose-300 rounded-full">
                    Urgent
                  </span>
                )}
                {pinDays !== "0" && (
                  <span className="px-2 py-0.5 text-xs bg-amber-900/50 text-amber-300 rounded-full flex items-center gap-1">
                    <Pin className="w-3 h-3" /> Pinned {pinDays}d
                  </span>
                )}
              </div>
              <p className="text-white font-semibold">{title || "Untitled"}</p>
              <p className="text-slate-300 text-sm whitespace-pre-wrap leading-relaxed">
                {body}
              </p>
              <div className="flex items-center gap-2 pt-2 border-t border-slate-700">
                {REACTIONS.map((r) => (
                  <span key={r} className="text-lg opacity-60">
                    {r}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPreview(false)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
              >
                Edit
              </button>
              <button
                onClick={() => {
                  setShowPreview(false);
                  setShowConfirm(true);
                }}
                className="flex-1 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium"
              >
                Looks good — Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Confirm Send</h3>
                <p className="text-sm text-slate-500">
                  This cannot be edited or recalled after sending.
                </p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg px-4 py-3 text-sm text-slate-700 space-y-1">
              <p>
                <span className="text-slate-400">To:</span> All users in{" "}
                {selectedFacility}
              </p>
              <p>
                <span className="text-slate-400">Subject:</span> {title}
              </p>
              <p>
                <span className="text-slate-400">Priority:</span> {priority}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="flex-1 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Send Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BroadcastComposer;
